import { Component, Inject, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { throwError } from 'rxjs';

import { AuthService } from 'src/app/services/auth/auth.service';
import { AlertService } from 'src/app/services/alert/alert.service';
import { SeoService } from 'src/app/services/seo/seo.service';
import { environment } from 'src/environments/environment';
import { JwtRequest, JwtResponse } from 'src/app/interfaces/jwt';
import { RolesType } from 'src/app/enums/RolesType.enum';

const correoRegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

declare const google: any;

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {

  formLogin: FormGroup;
  mensajeError: string = null;
  hayError = false;
  enviandoLogin = false;
  cargandoSocial = false;
  showPassword = false;
  cuentaNoVerificada = false;
  correoNoVerificado = '';
  rolSeleccionado: 'candidato' | 'empresa' = 'candidato';
  private returnUrl: string = null;

  // ── WhatsApp-first login ───────────────────────────────────────────────────
  waStep: 'idle' | 'phone' | 'otp' | 'confirm' = 'idle';
  selectedDialCode = '521';
  waPhoneNumber = '';
  waPhoneError = '';
  waOtpCode = '';
  waSendingOtp = false;
  waVerifyingOtp = false;
  waOtpError = '';
  waMaskedEmail = '';
  waResolving = false;
  waResendCooldown = 0;
  private waResendTimer: any;

  readonly countries = [
    { dial: '521', display: '52', flag: '🇲🇽' },
    { dial: '1',   display: '1',  flag: '🇺🇸' },
    { dial: '57',  display: '57', flag: '🇨🇴' },
    { dial: '54',  display: '54', flag: '🇦🇷' },
    { dial: '56',  display: '56', flag: '🇨🇱' },
    { dial: '51',  display: '51', flag: '🇵🇪' },
    { dial: '593', display: '593', flag: '🇪🇨' },
    { dial: '58',  display: '58', flag: '🇻🇪' },
    { dial: '598', display: '598', flag: '🇺🇾' },
    { dial: '34',  display: '34', flag: '🇪🇸' },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private alertService: AlertService,
    private seo: SeoService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    this.seo.setDefault('Iniciar Sesión', 'Accede a TecGurus Empleos con Google, LinkedIn o tú correo para encontrar tu próximo empleo tech.');
  }

  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

    // Detectar intención del usuario (viene de "Publicar vacante" o similar)
    const intent = this.route.snapshot.queryParamMap.get('intent');
    if (intent === 'empresa') this.rolSeleccionado = 'empresa';
    else if (intent === 'candidato') this.rolSeleccionado = 'candidato';

    const mensaje = this.route.snapshot.queryParamMap.get('mensaje');
    if (mensaje === 'email_exists') {
      const tipo     = this.route.snapshot.queryParamMap.get('tipo')     || 'unknown';
      const provider = this.route.snapshot.queryParamMap.get('provider') || 'local';
      const providerLabel = provider === 'google' ? 'Google' : provider === 'linkedin' ? 'LinkedIn' : null;
      const tipoLabel = tipo === 'candidato' ? 'candidato' : tipo === 'empresa' ? 'empresa' : null;
      if (providerLabel && tipoLabel) {
        this.mensajeError = `Ya tienes una cuenta de ${tipoLabel}. Inicia sesión con ${providerLabel}.`;
      } else if (tipoLabel) {
        this.mensajeError = `Ya tienes una cuenta de ${tipoLabel}. Ingresa tu correo y contraseña.`;
      } else {
        this.mensajeError = 'El correo ya está registrado. Inicia sesión.';
      }
    }

    this.formLogin = this.formBuilder.group({
      email:    [null, [Validators.required, Validators.pattern(correoRegExp)]],
      password: [null, Validators.required]
    });

    if (isPlatformBrowser(this.platformId)) {
      this.initGoogleSignIn();
      this.checkLinkedInCallback();
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.waResendTimer);
  }

  // ── WhatsApp-first login ───────────────────────────────────────────────────

  get waFullPhone(): string {
    const digits = this.waPhoneNumber.replace(/\D/g, '');
    return this.selectedDialCode + digits;
  }

  onWaPhoneChange(value: string): void {
    this.waPhoneNumber = (value || '').replace(/\D/g, '');
  }

  onWaOtpChange(value: string): void {
    this.waOtpCode = (value || '').replace(/\D/g, '').slice(0, 6);
  }

  openWhatsapp(): void {
    this.waStep = 'phone';
    this.mensajeError = null;
  }

  closeWhatsapp(): void {
    this.waStep = 'idle';
    this.waPhoneNumber = '';
    this.waOtpCode = '';
    this.waPhoneError = '';
    this.waOtpError = '';
    clearInterval(this.waResendTimer);
  }

  sendWaOtp(): void {
    const digits = this.waPhoneNumber.replace(/\D/g, '');
    if (digits.length < 7) {
      this.waPhoneError = 'Ingresa un número válido';
      return;
    }
    this.waPhoneError = '';
    this.waSendingOtp = true;
    this.authService.whatsappSendOtp(this.waFullPhone)
      .then(() => {
        this.waSendingOtp = false;
        this.waStep = 'otp';
        this.waOtpCode = '';
        this.waOtpError = '';
        this.startWaResendCooldown();
      })
      .catch(err => {
        this.waSendingOtp = false;
        this.waPhoneError = err?.error?.message || 'Error al enviar código';
      });
  }

  resendWaOtp(): void {
    if (this.waResendCooldown > 0 || this.waSendingOtp) return;
    this.waOtpCode = '';
    this.waOtpError = '';
    this.waSendingOtp = true;
    this.authService.whatsappSendOtp(this.waFullPhone)
      .then(() => {
        this.waSendingOtp = false;
        this.startWaResendCooldown();
      })
      .catch(err => {
        this.waSendingOtp = false;
        this.waOtpError = err?.error?.message || 'Error al reenviar código';
      });
  }

  private startWaResendCooldown(): void {
    clearInterval(this.waResendTimer);
    this.waResendCooldown = 45;
    this.waResendTimer = setInterval(() => {
      this.waResendCooldown--;
      if (this.waResendCooldown <= 0) clearInterval(this.waResendTimer);
    }, 1000);
  }

  verifyWaOtp(): void {
    const code = this.waOtpCode.trim();
    if (code.length < 6) {
      this.waOtpError = 'Ingresa el código completo de 6 dígitos';
      return;
    }
    this.waOtpError = '';
    this.waVerifyingOtp = true;
    this.authService.whatsappVerifyOtp(this.waFullPhone, code)
      .then(result => {
        this.waVerifyingOtp = false;
        if (result?.requiresConfirmation) {
          this.waMaskedEmail = result.maskedEmail || '';
          this.waStep = 'confirm';
          return;
        }
        if (result?.data?.token) {
          this.guardarDatosUsuario(result.data);
        } else {
          this.waOtpError = 'No se pudo verificar. Intenta de nuevo.';
        }
      })
      .catch(err => {
        this.waVerifyingOtp = false;
        this.waOtpError = err?.error?.message || 'Código incorrecto. Solicita uno nuevo.';
        this.waOtpCode = '';
      });
  }

  resolveWa(action: 'LINK' | 'NEW'): void {
    this.waResolving = true;
    this.authService.whatsappResolve(this.waFullPhone, action)
      .then(result => {
        this.waResolving = false;
        if (result?.data?.token) {
          this.guardarDatosUsuario(result.data);
        } else {
          this.waOtpError = 'No se pudo completar. Intenta de nuevo.';
          this.waStep = 'otp';
        }
      })
      .catch(err => {
        this.waResolving = false;
        this.mensajeError = err?.error?.message || 'Ocurrió un error. Intenta de nuevo.';
        this.waStep = 'phone';
      });
  }

  // ── Email/password login ──────────────────────────────────────────────────

  iniciarSesion(): void {
    this.enviandoLogin = true;
    this.cuentaNoVerificada = false;
    this.correoNoVerificado = '';
    if (!this.formLogin.valid) return;

    const req: JwtRequest = {
      email:    this.formLogin.value.email.trim(),
      password: this.formLogin.value.password.trim()
    };

    this.authService.acceder(req)
      .then(result => {
        this.hayError = result.success;
        if (result.success) {
          if (result.data?.user) {
            this.guardarDatosUsuario(result.data);
          } else {
            this.hayError = false;
            this.mensajeError = 'Cuenta no verificada. Revisa tú correo con el código enviado.';
            this.cuentaNoVerificada = true;
            this.correoNoVerificado = this.formLogin.value.email?.trim() ?? '';
          }
        }
      })
      .catch(error => {
        this.hayError = false;
        const msg = error?.error?.message;
        if (msg === 'INVALID_CREDENTIALS') {
          this.mensajeError = 'El correo o contraseña son incorrectos.';
        } else if (msg === 'CUENTA_NO_VERIFICADA') {
          this.cuentaNoVerificada = true;
          this.correoNoVerificado = this.formLogin.value.email?.trim() ?? '';
          this.mensajeError = 'Debes verificar tu correo antes de iniciar sesión.';
        } else {
          this.mensajeError = 'Ocurrió un error al iniciar sesión. Intenta de nuevo.';
        }
        return throwError(error);
      })
      .finally(() => this.reiniciarMensaje());
  }

  // ── Google Sign-In ────────────────────────────────────────────────────────

  private initGoogleSignIn(): void {
    const scriptId = 'google-gsi-script';
    const render = () => {
      if (typeof google === 'undefined' || !google?.accounts?.id) return;
      google.accounts.id.initialize({
        client_id: environment.googleClientId,
        callback: (response: any) => this.handleGoogleResponse(response),
      });
      const container = document.getElementById('google-btn-container');
      if (container) {
        google.accounts.id.renderButton(container, {
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          locale: 'es',
          width: container.offsetWidth || 380,
        });
      }
    };
    if (document.getElementById(scriptId)) { render(); return; }
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = render;
    document.head.appendChild(script);
  }

  private handleGoogleResponse(response: any): void {
    if (!response?.credential) return;
    this.cargandoSocial = true;
    // Pasar el rol seleccionado — para usuarios nuevos crea el tipo correcto;
    // para usuarios existentes el backend detecta el tipo real de su cuenta
    this.authService.googleLogin(response.credential, this.rolSeleccionado, false)
      .then(result => {
        if (result?.data) this.guardarDatosUsuario(result.data);
        else this.mensajeError = 'No se pudo iniciar sesión con Google.';
      })
      .catch((err) => {
        if (err?.error?.message === 'USER_NOT_FOUND') {
          this.mensajeError = 'No encontramos una cuenta con ese correo. ¿Aún no te registras?';
        } else {
          this.mensajeError = 'Error al iniciar sesión con Google.';
        }
      })
      .finally(() => { this.cargandoSocial = false; });
  }

  // ── LinkedIn Sign-In ──────────────────────────────────────────────────────

  loginLinkedIn(): void {
    const state = Math.random().toString(36).substring(2);
    localStorage.setItem('linkedin_oauth_state', state);
    localStorage.setItem('linkedin_oauth_rol', this.rolSeleccionado);
    localStorage.setItem('linkedin_oauth_login_only', 'false');
    if (this.returnUrl) {
      localStorage.setItem('login_return_url', this.returnUrl);
    }

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: environment.linkedinClientId,
      redirect_uri: environment.linkedinRedirectUri,
      state,
      scope: 'openid profile email',
    });

    // Redirect en la misma pestaña — el callback regresa a /oauth/callback/linkedin
    window.location.href = `https://www.linkedin.com/oauth/v2/authorization?${params}`;
  }

  private checkLinkedInCallback(): void {
    // Esta ruta es alcanzada después del redirect de LinkedIn
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const savedState = localStorage.getItem('linkedin_oauth_state');

    if (code && state && state === savedState) {
      localStorage.removeItem('linkedin_oauth_state');
      this.returnUrl = this.returnUrl || localStorage.getItem('login_return_url');
      const rol = localStorage.getItem('linkedin_oauth_rol') || 'candidato';
      localStorage.removeItem('linkedin_oauth_rol');
      const loginOnly = localStorage.getItem('linkedin_oauth_login_only') === 'true';
      localStorage.removeItem('linkedin_oauth_login_only');
      this.cargandoSocial = true;
      this.authService.linkedinLogin(code, rol, loginOnly)
        .then(result => {
          if (result?.data) this.guardarDatosUsuario(result.data);
          else this.mensajeError = 'No se pudo iniciar sesión con LinkedIn.';
        })
        .catch((err) => {
          const msg = err?.error?.message;
          if (msg === 'USER_NOT_FOUND') {
            this.mensajeError = 'No encontramos una cuenta con ese correo. ¿Aún no te registras?';
          } else {
            this.mensajeError = msg ? `LinkedIn: ${msg}` : 'Error al iniciar sesión con LinkedIn.';
          }
        })
        .finally(() => { this.cargandoSocial = false; });
    }
  }

  // ── Utilidades ────────────────────────────────────────────────────────────

  private guardarDatosUsuario(data: JwtResponse): void {
    localStorage.setItem('currentUser', JSON.stringify(data));
    this.authService.setCurrentUserValue(data);

    if (!isPlatformBrowser(this.platformId)) {
      this.router.navigate(['/']);
      return;
    }

    if (data.role === RolesType.EMPRESAS && data.user?.perfilCompleto === false) {
      window.location.href = '/completar-empresa';
      return;
    }

    const returnUrl = this.returnUrl || localStorage.getItem('login_return_url');
    localStorage.removeItem('login_return_url');

    if (returnUrl) {
      window.location.href = returnUrl;
    } else if (data.role === RolesType.ADMIN) {
      window.location.href = '/admin';
    } else if (data.role === RolesType.EMPRESAS) {
      window.location.href = '/empresa';
    } else if (data.role === RolesType.CANDIDATOS) {
      // Candidato nuevo → free onboarding directo, sin paso intermedio
      if (data.isNew) {
        window.location.href = '/free-onboarding';
      } else {
        this.decidirRedireccionCandidato(data.user?.idUsuario);
      }
    } else {
      window.location.href = '/';
    }
  }

  /** Candidato legacy sin freebie usado (ni descartado) → free-onboarding en vez de home. */
  private decidirRedireccionCandidato(candidatoId: number | undefined): void {
    if (!candidatoId) { window.location.href = '/'; return; }
    this.alertService.getEstado(candidatoId).subscribe({
      next: (res: any) => {
        const estado = res?.data || res;
        const elegible = !estado?.freebieUsado && !estado?.freebiePromptDismissed;
        window.location.href = elegible ? '/free-onboarding' : '/';
      },
      error: () => { window.location.href = '/'; }
    });
  }

  private reiniciarMensaje(): void {
    setTimeout(() => {
      this.enviandoLogin = false;
      this.mensajeError = null;
    }, 5500);
  }
}
